import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EChartsOption } from "echarts";
import { apiFetch } from "../lib/api.js";
import { useViewportFrame } from "../hooks/useViewportFrame.js";
import {
  KNOWLEDGE_USE_VIEW_OPTIONS,
  bucketLabel,
  bucketPillClass,
  formatShare,
  knowledgeUseCaption,
  knowledgeUseColumns,
  type KnowledgeUseColumn,
  type KnowledgeUseEntry,
  type KnowledgeUsePayload,
  type KnowledgeUseSession,
  type KnowledgeUseView,
  type KnowledgeUseWeek,
} from "../lib/knowledgeUse.js";
import { dashIfNull, formatLastCall } from "../lib/mcpUsage.js";
import { rangeLastDays } from "./AiUsageDashboard.js";
import { Chart } from "./Chart.js";
import { EmptyState, ErrorState, LoadingState } from "./States.js";

type Row = KnowledgeUseEntry | KnowledgeUseSession | KnowledgeUseWeek;

function cellText(column: KnowledgeUseColumn, row: Row, timeZone: string): string {
  const value = (row as Record<string, unknown>)[column.key];
  if (column.key === "started_at" || column.key === "last_read_at") return formatLastCall(value as string | null | undefined, timeZone);
  if (column.key === "share") return formatShare(value as number | null);
  if (typeof value === "number") return value.toLocaleString();
  return dashIfNull(value as string | null | undefined);
}

/**
 * Is the knowledge Atlas holds being served, read and skipped on the door?
 * Per session, the entries a query receipt would have demanded against what
 * the person read; the weekly trend of that share; and the entries most often
 * skipped. Laid out like the MCP usage tab so the three tabs read as siblings.
 */
export function KnowledgeUsePanel() {
  const [from, setFrom] = useState(() => rangeLastDays(30).from);
  const [to, setTo] = useState(() => rangeLastDays(30).to);
  const [view, setView] = useState<KnowledgeUseView>("entries");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<KnowledgeUsePayload | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);
  const rows: Row[] = data ? (view === "entries" ? data.entries : view === "sessions" ? data.sessions : data.weeks) : [];
  useViewportFrame(gridRef, { active: !loading && !error && rows.length > 0 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams({ from, to });
      setData(await apiFetch<KnowledgeUsePayload>(`/api/tenant/ai/usage/knowledge?${q.toString()}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const s = data?.summary;
  const cards: ReadonlyArray<readonly [string, string, string?]> = s
    ? [
        ["Sessions with queries", s.sql_sessions.toLocaleString(), `${s.sessions.toLocaleString()} sessions, ${s.people.toLocaleString()} people`],
        ["Demanded entries read", formatShare(s.demanded_read_share), "mean per session"],
        ["Read none", s.sessions_read_none.toLocaleString(), "sessions that read nothing demanded"],
        ["Read all", s.sessions_read_all.toLocaleString(), "sessions that read everything demanded"],
        ["Re-derived", s.re_derived.toLocaleString(), `${s.recalled.toLocaleString()} recalled, ${s.novel.toLocaleString()} new ground`],
        ["Entries never read", s.entries_never_read.toLocaleString(), `of ${s.accepted_entries.toLocaleString()} accepted`],
      ]
    : [];

  const trendOption = useMemo((): EChartsOption => {
    const weeks = data?.weeks ?? [];
    return {
      grid: { left: 48, right: 16, top: 24, bottom: 36 },
      tooltip: {
        trigger: "axis",
        valueFormatter: (v) => (typeof v === "number" ? `${v}%` : "—"),
      },
      xAxis: { type: "category", data: weeks.map((w) => w.week_start) },
      yAxis: { type: "value", min: 0, max: 100, name: "% read", axisLabel: { formatter: "{value}%" } },
      series: [
        {
          type: "line",
          name: "Demanded entries read",
          data: weeks.map((w) => (w.share == null ? null : Math.round(w.share * 100))),
          connectNulls: false,
          symbolSize: 8,
        },
      ],
    };
  }, [data]);

  const columns = knowledgeUseColumns(view);
  const caption = data ? knowledgeUseCaption(data.summary.recorded_since, data.time_zone) : null;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="toolbar">
        <div className="toolbarGroup">
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <span className="label">From</span>
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <span className="label">To</span>
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <span className="label">Show</span>
            <select className="select" value={view} onChange={(e) => setView(e.target.value as KnowledgeUseView)}>
              {KNOWLEDGE_USE_VIEW_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error ? <ErrorState detail={error} /> : null}
      {!error && loading ? <LoadingState /> : null}

      {!error && !loading && data ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            {cards.map(([label, value, note]) => (
              <div key={label} className="card" style={{ padding: 12 }}>
                <div className="muted" style={{ fontSize: 12 }}>
                  {label}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
                {note ? (
                  <div className="muted" style={{ fontSize: 12 }}>
                    {note}
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Demanded entries read, by week</div>
            {data.weeks.length === 0 ? <div className="muted">No sessions ran a query in this range.</div> : <Chart option={trendOption} height={220} />}
          </div>

          {rows.length > 0 ? (
            <div className="card adminList">
              <div className="tableWrap tableWrapFrame" ref={gridRef}>
                <table className="table">
                  <thead>
                    <tr>
                      {columns.map((c) => (
                        <th key={c.key} title={c.title}>
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i}>
                        {columns.map((c) => (
                          <td key={c.key} title={c.key === "name" ? (row as KnowledgeUseEntry).description : undefined}>
                            {c.key === "bucket" ? (
                              <span className={bucketPillClass((row as KnowledgeUseSession).bucket)}>{bucketLabel((row as KnowledgeUseSession).bucket)}</span>
                            ) : (
                              cellText(c, row, data.time_zone)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <EmptyState title={view === "entries" ? "No accepted entries." : "No door sessions in this range."} />
          )}

          {caption ? <div className="muted">{caption}</div> : null}
        </>
      ) : null}
    </div>
  );
}
